import { GetAulaInstruments } from "../../../../dao/GetAulaInstruments";
import {
    Button,
    Card,
    Checkbox,
    Col,
    ConfigProvider,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Row,
    Select,
    Space,
    Switch,
    Tag,
    Tooltip,
    Typography,
    Upload
} from 'antd';
import locale from "antd/es/locale/es_ES";
import { AuthContext } from "../../../../config/AuthContext";
import JutgeColors from "../../../../styles/JutgeColors";
import {
    UploadOutlined,
    MinusCircleOutlined,
    PlusOutlined,
    RobotOutlined,
    ThunderboltOutlined,
    TrophyOutlined,
    DesktopOutlined,
    CalendarOutlined,
    FileTextOutlined,
    PictureOutlined,
    CompassOutlined,
    ApartmentOutlined,
    LockOutlined,
    TagOutlined
} from '@ant-design/icons';
import { PostActivitat } from "../../../../dao/jutge/PostActivitat";
import { GetAules } from "../../../../dao/GetAules";
import { GetPracticaIdImagen } from "../../../../dao/GetPracticaIdImagen";
import { GetAulesIdConfiguracio } from "../../../../dao/GetAulesIdConfiguracio";
import { GetAulaRaConfig } from "../../../../dao/GetAulaRaConfig";
import { GetJavamonConfig, GetSceneNames } from "../../../../dao/jutge/GetJavamonConfig";
import { GetAulaDocuments, GetActivityDocuments, SaveActivityDocuments } from "../../../../dao/AulaDocuments";
import localeEs from 'antd/es/date-picker/locale/es_ES';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from "react";

const { Text, Title } = Typography;
const { TextArea } = Input;

interface Props {
    activitat: any;
    visible: boolean;
    onClose: (updated: boolean) => void;
    defaultTipus?: number; // 0: Activitat, 1: Pràctica
    fixedIsExamen?: number; // 0: No examen, 1: Forçar examen
    availablePracticas?: any[]; // Otras prácticas para seleccionar como bloqueo
}

const ConfiguracioModal: React.FC<Props> = (props: Props) => {
    const [loading, setLoading] = useState(false);
    const [aules, setAules] = useState<any[]>([]);
    const { userId } = useContext(AuthContext);

    const [preview, setPreview] = useState<string | null>(null);
    const [rasAula, setRasAula] = useState<any[]>([]);
    const [aulaConfig, setAulaConfig] = useState<any[]>([]);
    const [seasons, setSeasons] = useState<any[]>([]);
    const [aulaDocuments, setAulaDocuments] = useState<any[]>([]);
    const [sceneNames, setSceneNames] = useState<any[]>([]);

    const [form] = Form.useForm();

    // Determinar si es Pràctica o Activitat
    // Si viene fijado por props.defaultTipus (0 o 1), se respeta
    const isFixedTipus = props.defaultTipus !== undefined;
    const initialTipus = isFixedTipus
        ? props.defaultTipus
        : (props.activitat?.tipus_aula !== undefined ? props.activitat.tipus_aula : (props.activitat?.tipus || 0));

    const [currentTipus, setCurrentTipus] = useState<number>(initialTipus ?? 0);
    const isPractica = currentTipus === 1;
    const isPildores = currentTipus === 2;

    // Paleta de colores temáticos para diferenciar a simple vista
    const themeColor = isPildores ? "#722ed1" : isPractica ? "#fa8c16" : "#1890ff";
    const themeBgSoft = isPildores ? "#f9f0ff" : isPractica ? "#fff7e6" : "#e6f7ff";
    const themeBorderSoft = isPildores ? "#d3adf7" : isPractica ? "#ffd591" : "#91d5ff";
    const badgeText = isPildores ? "Conjunt de Píldores" : isPractica ? "Pràctica" : "Activitat";

    useEffect(() => {
        const loadData = async () => {
            setAules(await GetAules(userId + ""));
            try {
                const sc = await GetSceneNames();
                if (Array.isArray(sc)) {
                    setSceneNames(sc);
                }
            } catch (err) { }
            try {
                const seasonsData = await GetJavamonConfig();
                if (seasonsData && seasonsData.seasons) {
                    setSeasons(JSON.parse(seasonsData.seasons).filter((s: any) => s.active));
                }
            } catch (err) { }

            if (!props.activitat) return;

            let activeDocIds: number[] = [];
            if (props.activitat.id !== 0 && props.activitat.id !== "0") {
                try {
                    activeDocIds = await GetActivityDocuments(props.activitat.id + "");
                } catch (e) {
                    console.error("Error loading activity documents:", e);
                }
            }

            const rawTipus = isFixedTipus
                ? props.defaultTipus
                : (props.activitat.tipus_aula !== undefined ? props.activitat.tipus_aula : (props.activitat.tipus || 0));
            setCurrentTipus(rawTipus ?? 0);

            // Contexto IA y toggle IA
            const rawContext = props.activitat.context || "";
            const isNoContext = !rawContext || rawContext.trim().toLowerCase() === "no context";
            const useIaVal = !isNoContext;

            const isExamenInitial = props.fixedIsExamen !== undefined
                ? props.fixedIsExamen === 1
                : (Number(props.activitat.is_examen ?? props.activitat.isExamen) === 1);

            form.setFieldsValue({
                nom: props.activitat.nom,
                estat: props.activitat.estatr !== undefined ? props.activitat.estatr : props.activitat.estat,
                id_aula: props.activitat.id_aula,
                is_examen: isExamenInitial,
                is_concurs: props.activitat.is_concurs === 1,
                is_ide: props.activitat.is_ide === 1,
                use_ia: useIaVal,
                context: isNoContext ? "" : rawContext,
                tipus: rawTipus,
                id_season: props.activitat.id_season || 1,
                inici: props.activitat.inici ? dayjs(props.activitat.inici.toString().substring(0, 19).replace('T', ' '), 'YYYY-MM-DD HH:mm:ss') : null,
                final: props.activitat.final ? dayjs(props.activitat.final.toString().substring(0, 19).replace('T', ' '), 'YYYY-MM-DD HH:mm:ss') : null,
                javamons: props.activitat.javamons ?? 10,
                inst: props.activitat.inst,
                minim: props.activitat.minim,
                ras: (props.activitat.ras || []).map((ra: any) => ({
                    id: ra.id,
                    percentatge: ra.percentatge || 0,
                    instrument: ra.instrument
                })),
                escena: props.activitat.escena || '',
                documentIds: activeDocIds || [],
                id_bloqueo: props.activitat.id_bloqueo || undefined,
                tags: props.activitat.tags
                    ? (Array.isArray(props.activitat.tags)
                        ? props.activitat.tags
                        : props.activitat.tags.split(/[\s,]+/).map((t: string) => t.trim()).filter(Boolean))
                    : []
            });

            if (props.activitat.imagen) {
                let img = props.activitat.imagen;
                if (img.startsWith("base64:")) {
                    const parts = img.split(":");
                    img = parts[parts.length - 1];
                }
                setPreview(`data:image/jpeg;base64,${img}`);
            } else {
                try {
                    const res: any = await GetPracticaIdImagen(props.activitat.id + "");
                    if (res?.imagen) {
                        const cleaned = res.imagen.split(":");
                        setPreview(`data:image/png;base64,${cleaned[cleaned.length - 1]}`);
                    } else {
                        setPreview(null);
                    }
                } catch (err) {
                    setPreview(null);
                }
            }
        };

        loadData();
    }, [props.activitat, props.defaultTipus]);

    useEffect(() => {
        const fetchRas = async () => {
            const aulaId = form.getFieldValue("id_aula") || props.activitat?.id_aula;
            if (aulaId) {
                const res = await GetAulesIdConfiguracio(aulaId, 1);
                if (res && Array.isArray(res)) {
                    const allRas = res.flatMap((item: any) => item.list || []).map((ra: any) => ({
                        id: ra.id,
                        nom: ra.num,
                        descripcio: ra.desc
                    }));
                    setRasAula(allRas);
                } else {
                    setRasAula([]);
                }

                const resConfig = await GetAulaRaConfig(aulaId);
                setAulaConfig(resConfig || []);

                try {
                    const docs = await GetAulaDocuments(aulaId + "");
                    setAulaDocuments(docs || []);
                } catch (e) {
                    console.error("Error fetching aula documents in activity:", e);
                }
            }
        };
        fetchRas();
    }, [props.activitat, props.visible]);

    const handleCancel = () => {
        props.onClose(false);
        setPreview(null);
    };

    async function submitData(values: any) {
        try {
            setLoading(true);
            const imagenBase64 = preview ? preview.split(',')[1] : null;

            // Formateo de fechas estricto cara a cara local 'YYYY-MM-DD HH:mm:ss'
            const formattedInici = values.inici ? values.inici.format('YYYY-MM-DD HH:mm:ss') : null;
            const formattedFinal = values.final ? values.final.format('YYYY-MM-DD HH:mm:ss') : null;

            // Manejo de Contexto IA
            let finalContext = "no context";
            if (values.use_ia) {
                finalContext = values.context && values.context.trim() ? values.context.trim() : "no context";
            }

            const payload = {
                ...values,
                id: props.activitat?.id ?? 0,
                is_examen: props.fixedIsExamen !== undefined ? props.fixedIsExamen : (values.is_examen ? 1 : 0),
                is_concurs: values.is_concurs ? 1 : 0,
                is_ide: values.is_ide ? 1 : 0,
                context: finalContext,
                id_season: values.id_season || 1,
                id_usuari: userId,
                imagen: imagenBase64,
                escena: values.escena || null,
                tipus: isFixedTipus ? props.defaultTipus : values.tipus,
                inici: formattedInici,
                fin: formattedFinal,
                javamons: isPractica ? 0 : (values.javamons ?? 0),
                inst: "",
                id_bloqueo: values.id_bloqueo || null,
                tags: Array.isArray(values.tags) ? values.tags.map((t: string) => t.trim()).filter(Boolean).join(' ') : (values.tags || ''),
                ras: (values.ras || []).map((r: any) => ({
                    id: r.id,
                    percentatge: r.percentatge || 0,
                    instrument: r.instrument
                }))
            };

            const savedAct = await PostActivitat(payload);
            const actId = savedAct?.id || props.activitat?.id;

            if (actId && actId !== 0 && actId !== "0") {
                try {
                    await SaveActivityDocuments(actId + "", values.documentIds || []);
                } catch (e) {
                    console.error("Error saving active documents for activity:", e);
                }
            }

            message.success(props.activitat?.id ? "Activitat desada correctament" : "Activitat creada correctament");
            props.onClose(true);
        } catch (err) {
            message.error("Error desant l'activitat");
        } finally {
            setLoading(false);
        }
    }

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                background: themeBgSoft,
                                border: `1px solid ${themeBorderSoft}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: themeColor,
                                fontSize: 18
                            }}>
                                <ApartmentOutlined />
                            </div>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#1f1f1f' }}>
                                    {props.activitat?.id ? `Editar ${badgeText}: ${props.activitat.nom}` : `Nova ${badgeText}`}
                                </div>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                    Configuració general de l'activitat, dates i recursos
                                </div>
                            </div>
                        </div>
                        <Tag color={isPractica ? "orange" : "blue"} style={{ fontWeight: 700, borderRadius: 12, padding: '2px 10px' }}>
                            {badgeText.toUpperCase()}
                        </Tag>
                    </div>
                }
                open={props.visible}
                onCancel={handleCancel}
                footer={null}
                width={860}
                centered
                styles={{ body: { maxHeight: '82vh', overflowY: 'auto', padding: '16px 24px' } }}
            >
                <Form
                    form={form}
                    onFinish={submitData}
                    layout="vertical"
                    initialValues={{
                        tipus: initialTipus,
                        estat: 1,
                        is_examen: false,
                        is_concurs: false,
                        is_ide: false,
                        use_ia: false,
                        javamons: 10
                    }}
                >
                    {/* 🔹 CABECERA SUPERIOR: IMAGEN Y PREVIEW BANNER */}
                    <div style={{
                        background: '#fafafa',
                        borderRadius: 12,
                        border: '1px solid #f0f0f0',
                        padding: 16,
                        marginBottom: 20,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 20
                    }}>
                        <div style={{
                            width: 110,
                            height: 80,
                            borderRadius: 8,
                            border: '1px dashed #d9d9d9',
                            background: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            {preview ? (
                                <img
                                    src={preview}
                                    alt="Imatge activitat"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                <div style={{ textAlign: 'center', color: '#bfbfbf', fontSize: 11 }}>
                                    <PictureOutlined style={{ fontSize: 24, display: 'block', marginBottom: 4 }} />
                                    Sense imatge
                                </div>
                            )}
                        </div>

                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Imatge destacada</div>
                            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 10 }}>
                                Imatge de portada per a la llista d'activitats i el mapa del món
                            </div>
                            <Space>
                                <Upload
                                    beforeUpload={() => false}
                                    maxCount={1}
                                    showUploadList={false}
                                    onChange={(info) => {
                                        if (info.fileList.length > 0) {
                                            const file = info.fileList[0].originFileObj as File;
                                            const reader = new FileReader();
                                            reader.onloadend = () => {
                                                setPreview(reader.result as string);
                                            };
                                            reader.readAsDataURL(file);
                                        } else {
                                            setPreview(null);
                                        }
                                    }}
                                >
                                    <Button size="small" icon={<UploadOutlined />}>Pujar imatge</Button>
                                </Upload>
                                {preview && (
                                    <Button size="small" danger type="link" onClick={() => setPreview(null)}>
                                        Eliminar imatge
                                    </Button>
                                )}
                            </Space>
                        </div>
                    </div>

                    {/* 🔹 BLOQUE 1: INFORMACIÓN GENERAL */}
                    <Row gutter={16}>
                        <Col span={14}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}>Nom</span>}
                                name="nom"
                                rules={[{ required: true, message: "Introdueix el nom" }]}
                            >
                                <Input placeholder="Ex: Exercicis de Programació Modular" style={{ borderRadius: 6 }} />
                            </Form.Item>
                        </Col>

                        <Col span={10}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}>Tipus</span>}
                                name="tipus"
                                rules={[{ required: true, message: "Selecciona el tipus" }]}
                            >
                                <Select
                                    disabled={isFixedTipus}
                                    style={{ borderRadius: 6 }}
                                    onChange={(val) => setCurrentTipus(val)}
                                >
                                    <Select.Option value={0}>Activitat</Select.Option>
                                    <Select.Option value={1}>Pràctica</Select.Option>
                                    <Select.Option value={2}>Píldora</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={14}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}>Aula</span>}
                                name="id_aula"
                                rules={[{ required: true, message: "Selecciona l'aula" }]}
                            >
                                <Select
                                    placeholder="Selecciona una aula"
                                    style={{ borderRadius: 6 }}
                                    onChange={(val) => {
                                        GetAulesIdConfiguracio(val, 1).then((res) => {
                                            if (res && Array.isArray(res)) {
                                                const allRas = res.flatMap((item: any) => item.list || []).map((ra: any) => ({
                                                    id: ra.id,
                                                    nom: ra.num,
                                                    descripcio: ra.desc
                                                }));
                                                setRasAula(allRas);
                                            } else {
                                                setRasAula([]);
                                            }
                                        });
                                        GetAulaRaConfig(val).then((res) => setAulaConfig(res || []));
                                        GetAulaDocuments(val + "").then((res) => setAulaDocuments(res || []));
                                    }}
                                >
                                    {aules.map((aula: any) => (
                                        <Select.Option key={aula.id} value={aula.id}>
                                            {aula.nom}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={10}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}>Estat</span>}
                                name="estat"
                                rules={[{ required: true, message: "Selecciona l'estat" }]}
                            >
                                <Select style={{ borderRadius: 6 }}>
                                    <Select.Option value={1}>
                                        <Tag color="red">No visible</Tag>
                                    </Select.Option>
                                    <Select.Option value={3}>
                                        <Tag color={JutgeColors.colors.color3}>Actiu (Automàtic per dates)</Tag>
                                    </Select.Option>
                                    <Select.Option value={2}>
                                        <Tag color={JutgeColors.colors.color1}>Finalitzada</Tag>
                                    </Select.Option>
                                    <Select.Option value={5}>
                                        <Tag color="default">Ocult</Tag>
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* 🔹 BLOQUE TAGS PERSONALIZADOS */}
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label={
                                    <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <TagOutlined style={{ color: themeColor }} /> Tags identificatius
                                        <span style={{ fontSize: 11, fontWeight: 400, color: '#8c8c8c' }}>
                                            (Escriu i prem Enter per afegir paraules clau, p. ex. "bucles", "arrays", "tema1")
                                        </span>
                                    </span>
                                }
                                name="tags"
                                style={{ marginBottom: 16 }}
                            >
                                <Select
                                    mode="tags"
                                    style={{ width: '100%' }}
                                    placeholder="Afegeix etiquetes..."
                                    tokenSeparators={[',', ' ']}
                                    open={false}
                                    tagRender={(tagProps) => {
                                        const { label, closable, onClose } = tagProps;
                                        return (
                                            <Tag
                                                closable={closable}
                                                onClose={onClose}
                                                style={{
                                                    background: '#f0f5ff',
                                                    border: '1px solid #adc6ff',
                                                    color: '#1d39c4',
                                                    borderRadius: 12,
                                                    padding: '1px 8px',
                                                    fontWeight: 500,
                                                    fontSize: 12,
                                                    marginRight: 4
                                                }}
                                            >
                                                #{label}
                                            </Tag>
                                        );
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* 🔹 BLOQUE 2: DATES (HORA D'ESPANYA) */}
                    <div style={{
                        background: '#fafafa',
                        border: '1px solid #f0f0f0',
                        borderRadius: 10,
                        padding: '12px 16px',
                        marginBottom: 16
                    }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#434343', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CalendarOutlined style={{ color: themeColor }} /> Període de disponibilitat (Horari d'Espanya Peninsular)
                        </div>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Data d'inici"
                                    name="inici"
                                    style={{ marginBottom: 0 }}
                                >
                                    <DatePicker
                                        locale={localeEs}
                                        showTime={{ format: 'HH:mm:ss' }}
                                        format="YYYY-MM-DD HH:mm:ss"
                                        placeholder="Sense inici (des de sempre)"
                                        style={{ width: '100%', borderRadius: 6 }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    label="Data de finalització"
                                    name="final"
                                    style={{ marginBottom: 0 }}
                                >
                                    <DatePicker
                                        locale={localeEs}
                                        showTime={{ format: 'HH:mm:ss' }}
                                        format="YYYY-MM-DD HH:mm:ss"
                                        placeholder="Sense límit"
                                        style={{ width: '100%', borderRadius: 6 }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    {/* 🔹 BLOQUE 3: TOGGLES ESTILIZADOS (EXAMEN, CONCURS, IDE, IA) */}
                    <div style={{
                        background: '#fafafa',
                        border: '1px solid #f0f0f0',
                        borderRadius: 10,
                        padding: '14px 16px',
                        marginBottom: 16
                    }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#434343', marginBottom: 12 }}>
                            Modalitats i Opcions Especials
                        </div>
                        <Row gutter={[16, 12]}>
                            <Col span={12}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px 12px',
                                    background: '#fff',
                                    borderRadius: 8,
                                    border: '1px solid #e8e8e8'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                                            <TrophyOutlined style={{ color: '#faad14', marginRight: 6 }} /> Mode Examen
                                        </div>
                                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Restringeix accés segons horari</div>
                                    </div>
                                    <Form.Item name="is_examen" valuePropName="checked" noStyle>
                                        <Switch disabled={props.fixedIsExamen !== undefined} />
                                    </Form.Item>
                                </div>
                            </Col>

                            <Col span={12}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px 12px',
                                    background: '#fff',
                                    borderRadius: 8,
                                    border: '1px solid #e8e8e8'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                                            <ThunderboltOutlined style={{ color: '#13c2c2', marginRight: 6 }} /> Mode Concurs
                                        </div>
                                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Puntuació i rànquing competitiu</div>
                                    </div>
                                    <Form.Item name="is_concurs" valuePropName="checked" noStyle>
                                        <Switch />
                                    </Form.Item>
                                </div>
                            </Col>

                            <Col span={12}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px 12px',
                                    background: '#fff',
                                    borderRadius: 8,
                                    border: '1px solid #e8e8e8'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                                            <DesktopOutlined style={{ color: '#722ed1', marginRight: 6 }} /> Usa IDE Theia
                                        </div>
                                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Obre entorn integrat de desenvolupament</div>
                                    </div>
                                    <Form.Item name="is_ide" valuePropName="checked" noStyle>
                                        <Switch />
                                    </Form.Item>
                                </div>
                            </Col>

                            <Col span={12}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px 12px',
                                    background: '#fff',
                                    borderRadius: 8,
                                    border: '1px solid #e8e8e8'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                                            <RobotOutlined style={{ color: '#eb2f96', marginRight: 6 }} /> Habilitar IA amb Context
                                        </div>
                                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Defineix instruccions i context per la IA</div>
                                    </div>
                                    <Form.Item name="use_ia" valuePropName="checked" noStyle>
                                        <Switch />
                                    </Form.Item>
                                </div>
                            </Col>

                            {isPractica && (
                                <Col span={24}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '10px 14px',
                                        background: '#fff',
                                        borderRadius: 8,
                                        border: '1px solid #ffd591'
                                    }}>
                                        <div style={{ flex: 1, marginRight: 16 }}>
                                            <div style={{ fontWeight: 600, fontSize: 13, color: '#d46b08' }}>
                                                <LockOutlined style={{ marginRight: 6 }} /> Bloqueo de acceso (Prerrequisito)
                                            </div>
                                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                                En modo desafío y juego, no se podrá acceder a esta práctica hasta haber intentado al menos una vez la práctica seleccionada.
                                            </div>
                                        </div>
                                        <div style={{ minWidth: 260 }}>
                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(prev, curr) => prev.id_aula !== curr.id_aula}
                                            >
                                                {({ getFieldValue }) => {
                                                    const currentAulaId = getFieldValue('id_aula') ?? props.activitat?.id_aula;
                                                    const filteredPracticas = (props.availablePracticas || []).filter((p: any) => {
                                                        if (p.id === props.activitat?.id) return false;
                                                        // Solo prácticas (tipus === 1 o tipus_aula === 1)
                                                        const pTipus = p.tipus_aula !== undefined ? p.tipus_aula : (p.tipus ?? 0);
                                                        if (pTipus !== 1) return false;
                                                        // Solo de la misma aula si la práctica actual tiene aula asignada
                                                        if (currentAulaId !== undefined && currentAulaId !== null && p.id_aula !== undefined && p.id_aula !== null) {
                                                            return String(p.id_aula) === String(currentAulaId);
                                                        }
                                                        return true;
                                                    });

                                                    return (
                                                        <Form.Item name="id_bloqueo" noStyle>
                                                            <Select
                                                                allowClear
                                                                placeholder="Sin bloqueo (Acceso libre)"
                                                                style={{ width: '100%' }}
                                                                showSearch
                                                                optionFilterProp="children"
                                                                filterOption={(input, option: any) => {
                                                                    const text = Array.isArray(option?.children)
                                                                        ? option.children.join(' ')
                                                                        : String(option?.children ?? option?.label ?? '');
                                                                    return text.toLowerCase().includes(input.toLowerCase());
                                                                }}
                                                            >
                                                                {filteredPracticas.map((p: any) => (
                                                                    <Select.Option key={p.id} value={p.id}>
                                                                        🔒 {p.nom}
                                                                    </Select.Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    );
                                                }}
                                            </Form.Item>
                                        </div>
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </div>

                    {/* 🔹 BLOQUE 4: CONTEXTO IA (SOLO VISIBLE SI TOGGLE IA ESTÁ ACTIVO) */}
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.use_ia !== curr.use_ia}>
                        {({ getFieldValue }) => {
                            const useIa = getFieldValue('use_ia');
                            if (!useIa) return null;
                            return (
                                <div style={{
                                    background: '#fff0f6',
                                    border: '1px solid #ffadd2',
                                    borderRadius: 10,
                                    padding: 16,
                                    marginBottom: 16
                                }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#c41d7f', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <RobotOutlined /> Context i Instruccions de la IA
                                    </div>
                                    <div style={{ fontSize: 12, color: '#780650', marginBottom: 10 }}>
                                        Instruccions específiques que guien la personalitat i límits de l'assistent per a aquesta activitat.
                                    </div>
                                    <Form.Item
                                        name="context"
                                        rules={[{ required: true, message: "Introdueix el context de l'activitat" }]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <TextArea
                                            rows={5}
                                            placeholder="Ex: Ets un tutor de Java especialitzat en estructures de dades. Dóna pistes sense resoldre el codi complet..."
                                            style={{ borderRadius: 6 }}
                                        />
                                    </Form.Item>
                                </div>
                            );
                        }}
                    </Form.Item>

                    {/* 🔹 BLOQUE 5: JAVAMONS (AGRUPADOS Y SÓLO EN ACTIVITATS) */}
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.tipus !== curr.tipus || prev.is_examen !== curr.is_examen || prev.is_concurs !== curr.is_concurs}>
                        {({ getFieldValue }) => {
                            const tipusVal = isFixedTipus ? props.defaultTipus : getFieldValue('tipus');
                            const isExamen = getFieldValue('is_examen');
                            const isConcurs = getFieldValue('is_concurs');

                            // Javamons solo en Activitats (tipus === 0) y cuando no es examen ni concurso
                            if (tipusVal === 0 && !isExamen && !isConcurs) {
                                return (
                                    <div style={{
                                        background: 'linear-gradient(135deg, #f6ffed 0%, #e6fffb 100%)',
                                        border: '1px solid #b7eb8f',
                                        borderRadius: 10,
                                        padding: 16,
                                        marginBottom: 16
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#237804', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{ fontSize: 16 }}>⚔️</span> Configuració Javamon (Modo Desafío)
                                            </div>
                                            <Tag color="green" style={{ borderRadius: 10, margin: 0 }}>Exclusiu d'Activitats</Tag>
                                        </div>

                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="id_season"
                                                    label={<span style={{ fontWeight: 600 }}>Temporada Javamon</span>}
                                                    rules={[{ required: true, message: "Selecciona temporada" }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Select placeholder="Selecciona temporada" style={{ borderRadius: 6 }}>
                                                        {seasons.map((s: any) => (
                                                            <Select.Option key={s.id} value={s.id}>{s.nom}</Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>

                                            <Col span={12}>
                                                <Form.Item
                                                    name="javamons"
                                                    label={<span style={{ fontWeight: 600 }}>Probabilitat Javamon (0 - 100 %)</span>}
                                                    tooltip="Percentatge d'aparició d'un Javamon en reclamar recompenses a Modo Desafío (0 = Mai, 100 = Sempre)"
                                                    rules={[
                                                        { required: true, message: "Introdueix un valor" },
                                                        { type: 'number', min: 0, max: 100, message: "Ha de ser entre 0 i 100" }
                                                    ]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        max={100}
                                                        addonAfter="%"
                                                        style={{ width: '100%', borderRadius: 6 }}
                                                        placeholder="0 (Mai) - 100 (Sempre)"
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>

                    {/* 🔹 BLOQUE 6: ESCENARIO UNITY */}
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}><CompassOutlined style={{ marginRight: 6 }} />Escena Zelda (Escenari de Destí)</span>}
                                name="escena"
                                tooltip="Selecciona l'escena de Unity a la qual es transportarà al jugador (configurades a 'Nombres visibles de Escenas')"
                            >
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Selecciona una escena (ej: Pueblo 0, Pueblo 1...)"
                                    optionFilterProp="label"
                                    options={sceneNames.map((sc: any) => ({
                                        value: sc.scene_name,
                                        label: `${sc.scene_name}${sc.display_name ? ` — ${sc.display_name}` : ''}`
                                    }))}
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* 🔹 BLOQUE 7: RESULTATS D'APRENENTATGE (RAs) */}
                    <div style={{
                        background: '#fafafa',
                        border: '1px solid #f0f0f0',
                        borderRadius: 10,
                        padding: 16,
                        marginBottom: 16
                    }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#434343', marginBottom: 12 }}>
                            Resultats d'Aprenentatge (RAs) Associats
                        </div>

                        <Form.List name="ras">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Row
                                            key={key}
                                            gutter={8}
                                            align="middle"
                                            style={{
                                                marginBottom: 8,
                                                background: '#fff',
                                                border: '1px solid #e8e8e8',
                                                padding: '10px 12px',
                                                borderRadius: 6
                                            }}
                                        >
                                            <Col span={10}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'id']}
                                                    label="RA"
                                                    rules={[{ required: true, message: 'Falta RA' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Select
                                                        placeholder="Selecciona RA"
                                                        onChange={() => {
                                                            const currentRas = form.getFieldValue('ras');
                                                            if (currentRas && currentRas[name]) {
                                                                currentRas[name].instrument = undefined;
                                                                form.setFieldsValue({ ras: currentRas });
                                                            }
                                                        }}
                                                    >
                                                        {rasAula.map((ra: any) => (
                                                            <Select.Option key={ra.id} value={ra.id}>
                                                                {ra.nom} - {ra.descripcio}
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>

                                            <Col span={12}>
                                                <Form.Item
                                                    noStyle
                                                    shouldUpdate={(prev, curr) => {
                                                        return prev.ras?.[name]?.id !== curr.ras?.[name]?.id || prev.id_aula !== curr.id_aula;
                                                    }}
                                                >
                                                    {({ getFieldValue }) => {
                                                        const ras = getFieldValue('ras') || [];
                                                        const currentRaId = ras[name]?.id;
                                                        const currentRaObj = rasAula.find((r: any) => r.id === currentRaId);
                                                        const raName = currentRaObj ? currentRaObj.nom : '';

                                                        const instruments = (currentRaObj && aulaConfig)
                                                            ? aulaConfig.filter((c: any) =>
                                                                c.nom_ra === raName ||
                                                                c.nom_ra === `RA${raName}` ||
                                                                (c.nom_ra && c.nom_ra.replace(/^RA/, '') === raName)
                                                            ).map((c: any) => c.instrument)
                                                            : [];

                                                        const uniqueInstruments = Array.from(new Set(instruments));

                                                        return (
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'instrument']}
                                                                label="Instrument"
                                                                rules={[{ required: true, message: 'Falta Instrument' }]}
                                                                style={{ marginBottom: 0 }}
                                                            >
                                                                <Select placeholder="Selecciona Instrument" disabled={!currentRaId}>
                                                                    {uniqueInstruments.map((inst: any) => (
                                                                        <Select.Option key={inst} value={inst}>
                                                                            {inst}
                                                                        </Select.Option>
                                                                    ))}
                                                                </Select>
                                                            </Form.Item>
                                                        );
                                                    }}
                                                </Form.Item>
                                            </Col>
                                            <Col span={2} style={{ textAlign: 'center' }}>
                                                <Form.Item name={[name, 'percentatge']} noStyle hidden>
                                                    <Input />
                                                </Form.Item>
                                                <MinusCircleOutlined
                                                    onClick={() => remove(name)}
                                                    style={{ color: '#ff4d4f', fontSize: 16, cursor: 'pointer' }}
                                                />
                                            </Col>
                                        </Row>
                                    ))}
                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        block
                                        icon={<PlusOutlined />}
                                        style={{ marginTop: fields.length > 0 ? 8 : 0, borderRadius: 6 }}
                                    >
                                        Afegir RA
                                    </Button>
                                </>
                            )}
                        </Form.List>
                    </div>

                    {/* 🔹 BLOQUE 8: RECURSOS DOCUMENTALS DE LA IA */}
                    <Form.Item
                        label={<span style={{ fontWeight: 600 }}>📄 Recursos de la IA Habilitats</span>}
                        name="documentIds"
                        tooltip="Selecciona els documents que la IA farà servir com a referència i font de resposta en aquesta activitat concreta."
                    >
                        <Checkbox.Group style={{ width: '100%' }}>
                            <div style={{ background: '#fafafa', padding: 14, borderRadius: 8, border: '1px solid #e8e8e8' }}>
                                {aulaDocuments.length === 0 ? (
                                    <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                                        No hi ha cap document de referència disponible en aquesta aula. Puja fitxers des de l'edició de l'aula.
                                    </span>
                                ) : (
                                    <Row gutter={[16, 8]}>
                                        {aulaDocuments.map((doc: any) => (
                                            <Col span={12} key={doc.id}>
                                                <Checkbox value={doc.id}>
                                                    <span style={{ fontWeight: 500, fontSize: 13 }}>{doc.nom}</span>
                                                    <span style={{ color: '#8c8c8c', fontSize: '0.8rem', marginLeft: '6px' }}>
                                                        ({doc.mida ? `${(doc.mida / 1024).toFixed(1)} KB` : '0 KB'})
                                                    </span>
                                                </Checkbox>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </div>
                        </Checkbox.Group>
                    </Form.Item>

                    {/* 🔹 BOTONES ACCIÓN */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
                        <Button onClick={handleCancel} style={{ borderRadius: 6 }}>
                            Cancel·lar
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            style={{
                                backgroundColor: themeColor,
                                borderColor: themeColor,
                                borderRadius: 6,
                                padding: '0 24px',
                                fontWeight: 600
                            }}
                        >
                            {props.activitat?.id ? "Guardar canvis" : `Crear ${badgeText}`}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default ConfiguracioModal;
